import { useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { CUSTOMER_COUNTRY_CONFIG_KEY } from "../utils/productTradability";
import { hasProductRestrictions } from "../utils/productRestrictions";
import type { ProductRestrictionFlag } from "../types/productV3";

interface TradabilityCandidate {
  restrictionFlags?: readonly ProductRestrictionFlag[] | null;
}

export interface UseProductTradabilityResult {
  /** Raw `customer_country` from user config; null when unset (treated as US). */
  customerCountry: string | null;
  /** True while the globally enabled tradability policy is active. */
  isEnforced: boolean;
  /** True when this product carries no restriction flags for the signed-in customer. */
  isTradable: (product: TradabilityCandidate | null | undefined) => boolean;
}

/**
 * Per-customer trading eligibility for a product.
 *
 * The verdict is the backend's: investor product search evaluates the signed-in
 * user's profile (`customer_country` + `isretirement`) against each product's
 * rolled-up contract remarks and returns `restrictionFlags` on every item. An
 * empty array means the account can trade the whole product, so the client only
 * has to ask whether any flag is present. This hook is the single policy source
 * for every add, save, and adopt warning surface.
 *
 * `customerCountry` is read from `user_configuration.data` for display only (the
 * tradability modal header). The config is fetched as part of the auth bootstrap
 * (AuthContext awaits it before auth resolves), so there is no separate load
 * state here.
 *
 * Products sourced from provider or admin product search always carry an empty
 * `restrictionFlags` (the backend does not evaluate a profile there), so those
 * surfaces show no restriction warnings by construction.
 */
export function useProductTradability(): UseProductTradabilityResult {
  const { getUserConfigValue } = useAuth();

  const customerCountry = getUserConfigValue(CUSTOMER_COUNTRY_CONFIG_KEY);
  const isEnforced = true;

  const isTradable = useCallback(
    (product: TradabilityCandidate | null | undefined): boolean => {
      if (!product) return true;
      return !hasProductRestrictions(product.restrictionFlags);
    },
    []
  );

  return useMemo(
    () => ({ customerCountry, isEnforced, isTradable }),
    [customerCountry, isEnforced, isTradable]
  );
}
